<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { reactive, ref } from 'vue'
import { useMutation } from '@vue/apollo-composable'
import gql from 'graphql-tag'
import useVuelidate from '@vuelidate/core'
import { normalName, required } from '@/validation'
import TextField from '@/components/input/TextField.vue'

type StepLoadResult = {
  article: {
    loadEbomFromStepFile: {
      error: string | null
      ebom: { id: string } | null
    }
  }
}

const props = defineProps<{
  bopId: string
  uploadId: string
}>()
const emit = defineEmits<{
  done: [value: string]
}>()

const { t } = useI18n()

const model = reactive({ ebomName: '' })
const validation = useVuelidate({ ebomName: { required, normalName } }, model)

const mutation = useMutation<
  StepLoadResult,
  { command: { uploadId: string; bopId: string; ebomName: string } }
>(gql`
  mutation StepEbomLoaderLoad($command: LoadEbomFromStepFileCommand!) {
    article {
      loadEbomFromStepFile(command: $command) {
        error
        ebom {
          id
        }
      }
    }
  }
`)

const processingError = ref<string>()

async function onProcess() {
  validation.value.$touch()
  if (validation.value.$invalid) return

  processingError.value = undefined
  await mutation
    .mutate({
      command: {
        bopId: props.bopId,
        uploadId: props.uploadId,
        ebomName: model.ebomName,
      },
    })
    .then((result) => {
      const loadResult = result?.data?.article.loadEbomFromStepFile
      if (!loadResult) return
      if (loadResult.error) {
        processingError.value = loadResult.error
        return
      }
      emit('done', loadResult.ebom!.id)
    })
    .catch(() => {
      processingError.value = 'unknown'
    })
}
</script>

<template>
  <text-field
    v-model="model.ebomName"
    required
    :label="t('component.stepEbomLoader.ebomName')"
    :validation="validation.ebomName as any"
  />

  <v-alert v-if="processingError" type="error" class="mt-3">
    {{ processingError }}
  </v-alert>

  <div class="text-right mt-5">
    <v-btn
      color="primary"
      :disabled="validation.$invalid"
      :loading="mutation.loading.value"
      @click="onProcess"
    >
      {{ t('component.stepEbomLoader.buttonProcess') }}
    </v-btn>
  </div>
</template>

<style scoped lang="scss"></style>
